import PropTypes from 'prop-types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import './TableNamesCell.css';

/**
 * Every table a booking or bill sits at, as "first table +N" — the merchant
 * app's Transactions "Items Name" cell, for tables. Clicking +N opens a card
 * listing them all. One table shows just its name; none shows a dash.
 *
 * `tables` is a list of `{ name, capacity? }` (host table first, then any
 * merged in) — see `bookingTables` below to build it from a booking.
 */
export default function TableNamesCell({ tables, emptyLabel = '—', showSeated = false }) {
  const list = (tables || []).filter((t) => t && t.name);
  if (list.length === 0) return <span className="tnc-empty">{emptyLabel}</span>;

  const first = list[0];
  const extra = list.length - 1;

  const popover = (
    <Popover className="tnc-popover" onClick={(e) => e.stopPropagation()}>
      <div className="tnc-head">
        <span className="tnc-ring">
          <span />
        </span>
        <div>
          <div className="tnc-title">Tables</div>
          <div className="tnc-sub">{list.length} tables total</div>
        </div>
      </div>
      <div className="tnc-rows">
        {list.map((t, i) => (
          <div className="tnc-row" key={`${t.name}-${i}`}>
            <span className="tnc-num">{i + 1}</span>
            <span className="tnc-name">{t.name}</span>
            {showSeated && t.seated != null && <span className="tnc-cap">{t.seated} seated</span>}
          </div>
        ))}
      </div>
    </Popover>
  );

  return (
    <span className="tnc">
      <span className="tnc-first" title={list.map((t) => t.name).join(', ')}>
        {first.name}
        {showSeated && first.seated != null && <span className="tnc-cap ms-1">({first.seated} seated)</span>}
      </span>
      {extra > 0 && (
        <OverlayTrigger trigger="click" rootClose placement="bottom-start" overlay={popover}>
          <button type="button" className="tnc-pill" onClick={(e) => e.stopPropagation()}>
            +{extra}
          </button>
        </OverlayTrigger>
      )}
    </span>
  );
}

TableNamesCell.propTypes = {
  tables: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, capacity: PropTypes.number, seated: PropTypes.number })),
  emptyLabel: PropTypes.node,
  showSeated: PropTypes.bool
};

/**
 * A booking's `tables` (from the booking list / view) flattened to host table
 * then the tables merged into its sitting (`merged_tables`).
 */
export function bookingTables(tables) {
  return (tables || []).flatMap((t) => [
    { name: t.name, capacity: t.capacity },
    ...(t.merged_tables || []).map((m) => ({ name: m.name, capacity: m.capacity }))
  ]);
}

/**
 * Splits a party of [guests] across [tables] in order — the host table fills
 * to its capacity first, then each merged table — setting `seated` on each.
 * Which guest sits where is not recorded, so this is the occupancy the party
 * size implies. A table with no known capacity takes whatever is left.
 */
export function withSeated(tables, guests) {
  let left = Math.max(0, parseInt(guests, 10) || 0);
  return (tables || []).map((t) => {
    const cap = Number.isFinite(t.capacity) ? t.capacity : left;
    const seated = Math.min(cap, left);
    left -= seated;
    return { ...t, seated };
  });
}
